/**
 * `agent_action_type` — the kinds of action skyAgent may PROPOSE, shared by the
 * server that mints them and the card that renders them.
 *
 * **These are the SPEC NAMES**, because that is what is actually stored:
 * `actionRegistry` writes `action_type: spec.name` onto `skycrm_agent_actions`,
 * the approve route resolves the executor with `getCrmAction(row.actionType)`,
 * and the read routes hand the same string to the client. One vocabulary, end to
 * end, and it is the one the database already holds.
 *
 * This file previously declared a SECOND, prettier vocabulary (`log_activity`,
 * `draft_email`, …) carried on the action payload. Nothing consumed it — the
 * store had `spec.name` and never needed it — so it was a third naming of the
 * same concept, which is precisely the drift `stageType.ts` was promoted to
 * prevent. It is deleted rather than kept "in case the UI wants nicer names": the
 * card gets its nice names from `ACTION_LABELS`, keyed on these.
 *
 * Shared in lib/enums because the FE card and the server both read it, and a
 * hand-written copy on either side drifts silently — the card falls back to a
 * generic eyebrow rather than erroring, so a counselor is asked to approve a write
 * whose kind they cannot see.
 */

/**
 * v1 action set. Seven, and every one is `propose_*` — a prefix `registerCrmActions`
 * enforces, because the instruction and the UI both use it to tell a proposal from
 * a read, and a mutation hiding under a bare verb is the one the model narrates as
 * already done.
 */
export const AGENT_ACTION_TYPES = [
  "propose_log_activity",
  "propose_create_task",
  "propose_update_stage",
  "propose_draft_message",
  /** Ratified 2026-08-13, amending D6 ("scheduling a tour is explicitly out of
   *  v1"). Distinct from a task because it books a real appointment on a
   *  community's tour board, not just a reminder on someone's list. */
  "propose_schedule_tour",
  /** A durable skyBrain fact. Still a write, so it is still an approval card. */
  "propose_remember",
  /** Keep something the agent wrote, so it survives the chat. Nothing is ever
   *  sent from a draft — see `crmActionTools/proposeSaveDraft.ts`. */
  "propose_save_draft",
] as const;

export type AgentActionType = (typeof AGENT_ACTION_TYPES)[number];

/**
 * Narrow an unknown string from the wire or from a stored row.
 *
 * The action row round-trips through Postgres, so what comes back is `string` no
 * matter what went in. A row written by a newer deploy and read by an older one
 * must not be able to widen this type by assertion — the approve route already
 * fails closed on an unknown `action_type`, and the card must too.
 */
export function isAgentActionType(value: unknown): value is AgentActionType {
  return typeof value === "string" && (AGENT_ACTION_TYPES as readonly string[]).includes(value);
}
